import { useNavigate } from "#src/Routers/hooks";
import { AGENT_DISPLAY_NAME } from "#src/routes/investigations/consts";
import { useCreateInvestigation } from "#src/components/hooks/adapters/useInvestigations";
import {
  InvestigationComposer,
  type InvestigationComposerSubmitPayloadType,
} from "#src/routes/investigations/InvestigationComposer";
import { InvestigationsRoutePath } from "#src/routes/investigations";
import { InvestigationDetailRoutePath } from "#src/routes/investigations/[investigationId]";
import { useBreadcrumbsFromRoute } from "#src/utils/route";
import { InvestigationPageLayout } from "#src/routes/investigations/InvestigationPageLayout";
import {
  colours,
  generateDocumentTitle,
  Icon,
  Panel,
} from "@validereinc/common-components";
import {
  MAX_INVESTIGATION_MESSAGE_LENGTH,
  ResourceDefinitions,
} from "@validereinc/domain";
import classNames from "classnames/bind";
import React, { useCallback } from "react";
import { Titled } from "react-titled";
import styles from "./NewInvestigationPage.module.scss";

const cx = classNames.bind(styles);

export const NewInvestigationPage = () => {
  const navigate = useNavigate();
  const [breadcrumbs] = useBreadcrumbsFromRoute(InvestigationsRoutePath);
  const createInvestigation = useCreateInvestigation();

  const handleCreateInvestigation = useCallback(
    async ({ content, metadata }: InvestigationComposerSubmitPayloadType) => {
      const response = await createInvestigation.mutateAsync({
        content,
        metadata,
      });

      navigate({
        pathname: InvestigationDetailRoutePath.toLink({
          pathParams: { investigationId: response.data.investigation_id },
        }),
      });
    },
    [createInvestigation, navigate]
  );

  return (
    <Titled
      title={generateDocumentTitle(
        `New ${ResourceDefinitions.investigation.label.singular}`,
        InvestigationsRoutePath.title,
        { fallbackExistingTitle: "Validere" }
      )}
    >
      <InvestigationPageLayout
        title={InvestigationsRoutePath.title}
        setDocumentTitle={false}
        breadcrumbs={breadcrumbs}
      >
        <div className={cx("splash")}>
          <Panel
            isFluidY
            ariaLabel={AGENT_DISPLAY_NAME}
            className={cx("splash__panel")}
          >
            <div className={cx("splash__content")}>
              <div className={cx("splash__hero")}>
                <div className={cx("splash__title-block")}>
                  <Icon
                    variant="sparkle"
                    weight="fill"
                    colour={colours.colours.navy[800]}
                    size={50}
                  />
                  <h2 className={cx("splash__heading")}>
                    {AGENT_DISPLAY_NAME}
                  </h2>
                </div>
                <p className={cx("splash__subtitle")}>
                  What should we investigate?
                </p>
              </div>
              <div className={cx("splash__composer")}>
                <InvestigationComposer
                  textareaLabel="Prompt"
                  attachmentsInputId="investigation-composer-new-attachments"
                  placeholder={`Ask ${AGENT_DISPLAY_NAME}...`}
                  maxLengthErrorMessage={`Prompt must be ${MAX_INVESTIGATION_MESSAGE_LENGTH.toLocaleString()} characters or fewer.`}
                  isLoading={createInvestigation.isLoading}
                  onSubmit={handleCreateInvestigation}
                />
              </div>
            </div>
          </Panel>
        </div>
      </InvestigationPageLayout>
    </Titled>
  );
};
